import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Clock,
  Percent,
  FileText,
  DollarSign,
  TrendingUp,
  Database,
  Settings,
  Scan,
  Building2
} from 'lucide-react';
import nimbusLogoName from '../assets/logo-ephyco-name.png';
import nimbusLogoOnly from '../assets/logo-ephyco.png';
import { colors } from '../styles/colors';
import buildInfo from '../buildInfo.generated.js';
import { useAuth } from '../contexts/AuthContext';

function Sidebar({ isCollapsed }) {
  const location = useLocation();
  const { user } = useAuth();

  const isActive = (path) => {
    return location.pathname === path;
  };

  // URL du dashboard Supabase depuis variable d'environnement
  const supabaseDashboardUrl = process.env.REACT_APP_SUPABASE_URL || 'https://supabase.com/';

  const navLinks = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/temps', label: 'Saisie des temps', icon: Clock },
    { path: '/tmp', label: 'TMP', icon: Percent },
    { path: '/facturation', label: 'Facturation', icon: FileText },
    { path: '/charges', label: 'Charges', icon: DollarSign },
    { path: '/pl', label: 'P&L', icon: TrendingUp },
    { path: '/banking', label: 'Banques', icon: Building2 },
    { path: '/scan', label: 'Scanner facture', icon: Scan }
  ];

  return (
    <>
      <style>
        {`
          /* Scrollbar custom pour la navigation sidebar */
          .sidebar-nav-scroll::-webkit-scrollbar {
            width: 6px;
          }

          .sidebar-nav-scroll::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 3px;
            margin: 0.5rem 0;
          }

          .sidebar-nav-scroll::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 3px;
            transition: background 200ms ease-in-out;
          }

          .sidebar-nav-scroll::-webkit-scrollbar-thumb:hover {
            background: ${colors.primary.teal};
          }

          /* Firefox */
          .sidebar-nav-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
          }
        `}
      </style>
      <aside
        style={{
          width: isCollapsed ? '112px' : '288px',
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          transition: 'width 300ms ease-in-out',
          backgroundColor: colors.text.white,
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          boxSizing: 'border-box'
        }}
      >
      {/* Container avec bords arrondis */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: colors.primary.gray,
        borderRadius: '1rem',
        overflow: 'hidden'
      }}>
        {/* Logo Header */}
        <div style={{
          padding: isCollapsed ? '0.75rem' : '0.75rem',
          boxSizing: 'border-box',
          transition: 'padding 300ms ease-in-out',
        }}>
          <div style={{
            height: '48px',
            borderRadius: isCollapsed ? '1.5rem' : '0.75rem',
            backgroundColor: colors.secondary.lightGray,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 300ms ease-in-out',
            overflow: 'hidden'
          }}>
            <img
              src={isCollapsed ? nimbusLogoOnly : nimbusLogoName}
              alt="Logo Ephyco"
              style={{
                height: isCollapsed ? '60%' : '60%',
                width: 'auto',
                transition: 'all 300ms ease-in-out'
              }}
            />
          </div>
        </div>

        {/* Navigation */}
        <nav
          className="sidebar-nav-scroll"
          style={{
            flex: 1,
            padding: '0.5rem 0.75rem',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0
          }}>
            {navLinks.map((link) => {
              const Icon = link.icon;
              const active = isActive(link.path);

              return (
                <li key={link.path} style={{ marginBottom: '0.5rem' }}>
                  <Link
                    to={link.path}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '1rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '0.75rem',
                      textDecoration: 'none',
                      backgroundColor: active ? colors.primary.teal : 'transparent',
                      color: active ? colors.text.white : colors.text.white,
                      transition: 'all 200ms ease-in-out',
                      fontFamily: '"Noto Sans", sans-serif',
                      fontSize: '0.9375rem',
                      fontWeight: active ? '500' : '400',
                      position: 'relative',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    title={isCollapsed ? link.label : ''}
                  >
                    <Icon
                      size={22}
                      style={{
                        flexShrink: 0,
                        minWidth: '22px'
                      }}
                    />

                    <span style={{
                      transition: 'opacity 300ms ease-in-out',
                      opacity: isCollapsed ? 0 : 1,
                      width: isCollapsed ? 0 : 'auto'
                    }}>
                      {link.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Section Administration - super admin uniquement */}
        {user?.role === 'super_admin' && (
          <div style={{
            padding: '0.5rem 0.75rem',
            borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
          }}>
            {/* Lien Administration */}
            <div style={{ marginBottom: '0.5rem' }}>
              <Link
                to="/admin"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '1rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.75rem',
                  textDecoration: 'none',
                  backgroundColor: isActive('/admin') ? colors.primary.teal : 'transparent',
                  color: isActive('/admin') ? colors.text.white : colors.text.white,
                  transition: 'all 200ms ease-in-out',
                  fontFamily: '"Noto Sans", sans-serif',
                  fontSize: '0.9375rem',
                  fontWeight: isActive('/admin') ? '500' : '400',
                  position: 'relative',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (!isActive('/admin')) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/admin')) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                title={isCollapsed ? 'Administration' : ''}
              >
                <Settings
                  size={22}
                  style={{
                    flexShrink: 0,
                    minWidth: '22px'
                  }}
                />

                <span style={{
                  transition: 'opacity 300ms ease-in-out',
                  opacity: isCollapsed ? 0 : 1,
                  width: isCollapsed ? 0 : 'auto'
                }}>
                  Administration
                </span>
              </Link>
            </div>

            {/* Lien Airtable */}
            <div>
              <a
                href={supabaseDashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '1rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.75rem',
                  textDecoration: 'none',
                  backgroundColor: 'transparent',
                  color: colors.primary.gold,
                  transition: 'all 200ms ease-in-out',
                  fontFamily: '"Noto Sans", sans-serif',
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  position: 'relative',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(216, 174, 71, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title={isCollapsed ? 'Base de données Airtable' : ''}
              >
                <Database
                  size={22}
                  style={{
                    flexShrink: 0,
                    minWidth: '22px'
                  }}
                />

                <span style={{
                  transition: 'opacity 300ms ease-in-out',
                  opacity: isCollapsed ? 0 : 1,
                  width: isCollapsed ? 0 : 'auto'
                }}>
                  Base de données
                </span>
              </a>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '0.75rem 1rem',
          borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
          transition: 'all 300ms ease-in-out',
          overflow: 'hidden',
          height: '58px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            textAlign: 'center',
            transition: 'all 300ms ease-in-out',
            width: '100%'
          }}>
            <p style={{
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.6)',
              margin: 0,
              fontFamily: '"Noto Sans", sans-serif',
              opacity: isCollapsed ? 0 : 1,
              transition: 'opacity 300ms ease-in-out',
              lineHeight: isCollapsed ? 0 : '1',
              maxHeight: isCollapsed ? 0 : '100px',
              overflow: 'hidden'
            }}>
              © 2025 Ephyco
            </p>
            <p style={{
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.6)',
              margin: isCollapsed ? 0 : '0.25rem 0 0 0',
              fontFamily: '"Noto Sans", sans-serif'
            }}>
              v{buildInfo.version}{!isCollapsed && ` (${buildInfo.commitHash})`}
            </p>
          </div>
        </div>
      </div>
    </aside>
    </>
  );
}

export default Sidebar;
